/*
  Eva Radio: RADIO station mode. Independent layer, same pattern as
  eva-radio-tape.js -- owns its own DOM (station panel + toggle chip,
  see snippets/eva-radio.liquid) and its own <audio id="EvaRadioStationAudio">,
  never touching eva-radio.js/eva-radio-tape.js/eva-radio-viz.js's own
  code. Cross-file coordination is one-directional and event-based:
    - listens for the EXISTING 'eva:radio:playstate' event (dispatched by
      eva-radio.js whenever tape playback starts/stops) to stop a
      playing station the instant a tape starts.
    - calls the EXISTING public window.EvaRadioWidget.stop() to stop a
      playing tape the instant radio mode turns on.
  No edits needed to any other file for either direction.

  DEFINITIVE STATION RULE (2026-08-07, supersedes the original "two
  sources of stations" design): the player plays ONLY what's in
  assets/eva-stations.json -- Per's own curated list. There is no
  runtime query of any kind to the Radio Browser API or anywhere else
  -- TUNE (prev/next arrows) and SCAN both draw from a shuffle bag over
  that SAME list (2026-08-08, see stationBag/createShuffleBag() in the
  Presets section): every station is served exactly once, in a fresh
  random order, before any repeats, with silent auto-skip to the next
  bag entry on a dead stream so a change still lands somewhere real (or
  explicitly reports NO SIGNAL rather than hanging). Radio Browser is
  still used, but only offline, by the add-station skill's own
  search_station.py -- a lookup directory for Per to find real stream
  URLs to add to the manifest, never something this file fetches from
  at runtime.

  Plain <audio>, deliberately never WebAudio -- see the crossorigin
  comment on EvaRadioStationAudio in the liquid markup for why: third-
  party streams essentially never send CORS headers, and WebAudio
  silently fails (or throws) on a tainted cross-origin source. Gain
  control isn't needed here either -- stations arrive already mastered
  by the broadcaster, unlike tape's per-track loudness correction.
*/
(function () {
  function ready(fn) {
    if (document.readyState !== 'loading') {
      fn();
    } else {
      document.addEventListener('DOMContentLoaded', fn);
    }
  }

  // FIX (2026-08-09, "~20s hang" bug report): was 6000. Per's real-device
  // complaint traced to the WORST-CASE total of a retry chain, not a
  // single stalled connection -- see MAX_AUTO_SKIP_ATTEMPTS' own comment
  // and turnOn()'s resumeThenBagChain() for the other half of this fix.
  // 5000 matches the spec's own "~5s" ask exactly; every one of the 51
  // manifest stations was independently confirmed to connect in well
  // under 3s from a server-side probe the same day, so 5s of headroom
  // per attempt is still generous, not tight.
  var ATTEMPT_TIMEOUT_MS = 5000;

  // --- DOM refs --------------------------------------------------------
  var toggleBtn = null;
  var panel = null;
  var readout = null;
  var flagEl = null;
  var prevBtn = null;
  var nextBtn = null;
  var scanBtn = null;
  var stationAudio = null;
  var tapeRack = null;
  var rackHatch = null;
  var tunerScanEl = null;

  var presets = []; // from assets/eva-stations.json -- see loadManifest()
  var presetIndex = -1;
  var historyStack = []; // manifest indices actually listened to, oldest first -- BACK pops this; see the Presets section
  var stationModeOn = false;
  var attemptToken = 0; // bumped on every new attempt so a stale timeout/event from an abandoned attempt can't clobber a newer one

  function isMuted() {
    return window.EvaMute ? window.EvaMute.isMuted() : false;
  }

  // --- Tuning static (2026-08-07, rebuilt on Web Audio 2026-08-08, real
  // recorded static 2026-08-09) --------------------------------------
  // Was synthesized white noise; now a sprite of 12 real clips cut from
  // Per's own "radio static.wav" recording (crackle, tuning whistles,
  // heterodyne squeal, passing-over-a-station sweeps -- see the clip
  // list Per approved). One compressed asset (assets/radio-static-
  // sprite.mp3, mono 96kbps -- codec artifacts on noise are inaudible,
  // per Per, so compression costs nothing here), fetched once and
  // decoded into a single AudioBuffer; each play slices out one clip via
  // AudioBufferSourceNode.start(when, offset, duration) rather than
  // juggling 12 separate assets/elements. STATIC_CLIPS' offsets already
  // have a small inset built in relative to the sprite's own silence
  // pads (baked in at build time, see the clip-list report) so a slice
  // can't bleed into a neighbour even before the fade envelope below
  // adds its own margin.
  //
  // No band-limiting filters here (unlike the synthesized-noise version
  // this replaces) -- real static recorded through a real radio is
  // already naturally band-limited by that radio's own receiver/speaker,
  // so an artificial highpass/lowpass on top would just double-filter
  // it thinner than the source actually sounds. Real AudioParam fade
  // ramps are kept (start AND an early interrupt are both click-free,
  // not just the clip's own natural end -- a fast-connecting station
  // cuts static off well before 1.5s, the common case).
  //
  // LEVEL IS RELATIVE, NOT FIXED (2026-08-09, real change per Per):
  // static now plays at STATIC_LEVEL_RATIO of stationVolumeLevel, not a
  // standalone constant -- if a real music-volume control is ever added
  // to this widget, static automatically tracks it, because both read
  // the same stationVolumeLevel value rather than static owning its own
  // independent number. stationVolumeLevel is a SEPARATE variable from
  // stationAudio.volume, deliberately -- withMinVisibleTuning() (below)
  // already uses stationAudio.volume as a transient 0/1 hide-the-pop
  // toggle during the exact window static is playing, so reading
  // stationAudio.volume directly here would make static computed at ~0
  // for its entire natural lifetime. stationVolumeLevel is the STABLE
  // nominal level that transient trick doesn't touch.
  //
  // Per also asked to route static through "the same gain path as the
  // station audio" -- not done literally: stationAudio is a plain
  // <audio> element and this file's own top comment documents exactly
  // why it has to stay that way (third-party stream CORS -- Web Audio
  // silently fails on a tainted cross-origin source). Routing the real
  // station stream through this same WebAudio graph would risk breaking
  // playback outright. What's implemented instead is functionally
  // equivalent for the actual ask -- static's gain is DERIVED from
  // stationVolumeLevel every time it plays, so the two can never drift
  // apart -- without merging two audio sources that can't safely share
  // one graph. Flagging this deviation rather than silently doing
  // something different from the literal instruction.
  var stationVolumeLevel = 1; // the logical "current music volume" -- no UI control sets this yet (this codebase has only binary EvaMute, no continuous volume knob anywhere), but it's the one place a future control would act, and static already tracks it
  var STATIC_LEVEL_RATIO = 0.5; // static plays at 50% of stationVolumeLevel, per spec (supersedes the old fixed STATIC_LEVEL constant)
  var STATIC_FADE_S = 0.02; // 20ms linear fade in/out -- avoids the hard-gate click Per flagged
  var STATIC_STOP_FADE_S = 0.015; // fast ramp-to-zero used when stopStatic() truncates a clip early, same reasoning as the fade above

  // The sprite's own clip map -- offsets are in the SPRITE's timeline
  // (post-concatenation), not the original recording's. Regenerating
  // this list means regenerating assets/radio-static-sprite.mp3 too --
  // both are build-time outputs of the same offline step, so they're
  // kept together here as code rather than a separately-editable JSON
  // manifest (unlike the station/tape lists, which Per edits without
  // touching JS).
  var STATIC_CLIPS = [
    { start: 0.22, duration: 1.46 }, // static-swell
    { start: 1.92, duration: 1.46 }, // crackle-sequence
    { start: 3.62, duration: 1.46 }, // heterodyne-squeal
    { start: 5.32, duration: 1.46 }, // crackle-pulses
    { start: 7.02, duration: 1.46 }, // crackle-chirp
    { start: 8.72, duration: 1.46 }, // crackle-whistle
    { start: 10.42, duration: 1.46 }, // multi-crackle
    { start: 12.12, duration: 1.46 }, // crackle-decay
    { start: 13.82, duration: 1.46 }, // warbling-heterodyne
    { start: 15.52, duration: 1.46 }, // double-crackle
    { start: 17.22, duration: 1.46 }, // tuning-sweep
    { start: 18.92, duration: 1.46 } // sustained-whistle
  ];

  var staticCtx = null;
  var staticSpriteBuffer = null; // the whole sprite, decoded once -- individual plays slice out of this, never re-fetched/re-decoded
  var staticMuteGain = null; // persistent node, gain 0/1 -- kept separate from the per-play fade gain so a mute toggle mid-clip can't fight the fade envelope; implemented directly against the mute event since applyToElement() only knows HTMLMediaElement, not a Web Audio node
  var staticSource = null; // the currently-playing BufferSourceNode, if any -- lets a rapid second playStatic() (SCAN retries) cut it off instead of layering two clips
  var staticFadeGain = null; // the active per-play fade gain, if any -- stopStatic() ramps this to 0 rather than hard-stopping the source
  var staticChainCount = 0; // consecutive chained clips for the attempt currently in flight -- see MAX_STATIC_CHAIN_CLIPS, reset at the start of every fresh attempt in attemptPlay()
  var MAX_STATIC_CHAIN_CLIPS = 8; // hard backstop (2026-08-09, infinite-loop bugfix): even if some future bug slips a leak past isStaticChainAllowed() below, the chain physically cannot run unbounded again -- 8 * ~1.46s is comfortably longer than ATTEMPT_TIMEOUT_MS, so a real attempt always resolves well before this is ever the reason chaining stops

  function initStaticSprite() {
    try {
      var Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) { staticCtx = null; return; } // no Web Audio support -- fails silent, no tuning static, everything else unaffected
      staticCtx = new Ctx();

      staticMuteGain = staticCtx.createGain();
      staticMuteGain.gain.value = isMuted() ? 0 : 1;
      staticMuteGain.connect(staticCtx.destination);

      document.addEventListener(window.EvaMute ? window.EvaMute.EVENT : 'eva:sound:muted', function (e) {
        if (!staticMuteGain) { return; }
        staticMuteGain.gain.value = (e.detail && e.detail.muted) ? 0 : 1;
      });

      var url = window.EvaRadioStaticSpriteUrl;
      if (!url) { return; } // no sprite URL wired -- fails silent, same contract as above
      fetch(url).then(function (r) {
        if (!r.ok) { throw new Error('static sprite fetch failed'); }
        return r.arrayBuffer();
      }).then(function (buf) {
        return staticCtx.decodeAudioData(buf);
      }).then(function (decoded) {
        staticSpriteBuffer = decoded;
      }).catch(function () {
        staticSpriteBuffer = null; // fetch/decode failed -- no tuning static, everything else unaffected
      });
    } catch (e) {
      staticCtx = null;
    }
  }

  // Shuffle bag over the 12 clips, same permutation-queue as the station
  // list (see createShuffleBag() in the Presets section) -- a random
  // clip each channel change, no repeats until every clip's been used,
  // shared logic per Per's "reuse it, don't write a second
  // implementation." Created lazily on first use since createShuffleBag
  // is declared further down this file.
  var staticClipBag = null;
  function getStaticClipBag() {
    if (!staticClipBag) { staticClipBag = createShuffleBag(function () { return STATIC_CLIPS.length; }); }
    return staticClipBag;
  }

  // BUG FIX (2026-08-09): static could loop forever. Root cause, exactly
  // as Per diagnosed: the chain below used to continue "while staticActive
  // stays true," and staticActive was cleared ONLY by stopStatic(), which
  // was called from exactly one place -- attemptPlay()'s finish(). But
  // finish() no-ops on a stale token (`token !== attemptToken`) BY DESIGN,
  // so a superseded attempt's late resolve can't clobber a newer one. The
  // problem: turnOff() -> stopStation() bumps attemptToken to ABORT the
  // in-flight attempt, but that means finish() -- whenever it eventually
  // fires for the aborted attempt -- ALWAYS took the stale-token branch
  // and returned before ever reaching stopStatic(). staticActive stayed
  // true forever, onended kept chaining, and nothing else in the codebase
  // could reach the AudioBufferSourceNode to stop it (static is Web
  // Audio; stations are a plain <audio> element; the tape deck is a
  // separate file entirely) -- so it played over tape audio and survived
  // PJAX navigation (which doesn't tear this script down at all -- see
  // the module comment -- so there was never a moment anything could
  // have interrupted it).
  //
  // Fix has four parts:
  // 1. Every teardown path now calls stopStatic(), which hard-stops AND
  //    disconnects the source/gain nodes (not just clears a flag) --
  //    stopStation(), the eva:radio:playstate tape-start listener, and
  //    attemptPlay()'s finish() all call it now; previously only
  //    finish() did. See each call site's own comment.
  // 2. The chain condition is INVERTED: instead of "keep going unless
  //    something remembered to say stop" (staticActive, a flag that has
  //    to be explicitly cleared and can be forgotten), isStaticChainAllowed()
  //    below POSITIVELY verifies the attempt is still real -- stationModeOn
  //    is true AND the token this clip was started for still equals the
  //    live attemptToken. attemptToken is bumped by exactly the events
  //    that should end a chain (a new attempt starting, or stopStation()
  //    aborting one) -- checking against it directly can't leak the way a
  //    separately-maintained boolean can, because there's no second flag
  //    to forget to clear.
  // 3. Tape starting always calls stopStatic() now, unconditionally, at
  //    the top of the eva:radio:playstate listener -- not just as a side
  //    effect of stationModeOn happening to be true at that instant.
  // 4. MAX_STATIC_CHAIN_CLIPS is a hard cap on consecutive chained clips,
  //    checked inside isStaticChainAllowed() -- a backstop so a future
  //    bug in this logic can't reintroduce an unbounded loop even if it
  //    slips past the token/stationModeOn check.
  function isStaticChainAllowed(forToken) {
    return stationModeOn && forToken === attemptToken && staticChainCount < MAX_STATIC_CHAIN_CLIPS;
  }

  // Hard, immediate stop -- no fade, just .stop()+.disconnect() and clear
  // the refs. Used for the "cut off an overlapping clip" case inside
  // playStatic() itself (a new clip is about to start right now, so an
  // audible fade on the old one is pointless); stopStatic() below does
  // its own graceful fade-then-stop for the "something external is
  // ending this" case.
  function hardStopStaticSource() {
    if (!staticSource) { return; }
    try { staticSource.stop(); } catch (e) {}
    try { staticSource.disconnect(); } catch (e) {}
    if (staticFadeGain) { try { staticFadeGain.disconnect(); } catch (e) {} }
    staticSource = null;
    staticFadeGain = null;
  }

  // Fires at the START of every station-change attempt (TUNE arrows and
  // SCAN both funnel through attemptPlay() below), which passes forToken
  // (its own attemptToken snapshot) through so the chain below can tell,
  // at every link, whether it's still covering a genuinely live attempt --
  // see isStaticChainAllowed(). stopStatic() is called the instant that
  // attempt resolves, success or failure, so it can never overlap the
  // incoming stream (see attemptPlay()'s own finish()) and never outlasts
  // a failed attempt into a lingering no-signal state either.
  //
  // TIMING (2026-08-09): a real clip is 1.46s, not the old synthesized
  // burst's 350ms -- comfortably covers a typical fast Icecast handshake
  // on its own. For the slower tail (a real connection can take several
  // seconds, up to ATTEMPT_TIMEOUT_MS), onended below CHAINS into another
  // bag clip automatically for as long as isStaticChainAllowed() says
  // yes, so static keeps covering the gap for however long the attempt
  // actually takes -- per Per: "should cover the gap, not finish before
  // the new station arrives."
  function playStatic(forToken) {
    if (!staticCtx || !staticSpriteBuffer) { return; }
    try {
      if (staticCtx.state === 'suspended') { staticCtx.resume(); } // playStatic() only ever runs inside a user-gesture-triggered TUNE/SCAN click chain, so resuming here satisfies the autoplay-gesture requirement
      hardStopStaticSource(); // cut off an overlapping clip (rapid SCAN retries, or a chain link for an attempt that's ending right now) instead of layering two

      var clipIdx = getStaticClipBag().next();
      if (clipIdx < 0) { return; }
      var clip = STATIC_CLIPS[clipIdx];

      var source = staticCtx.createBufferSource();
      source.buffer = staticSpriteBuffer;

      var fadeGain = staticCtx.createGain();
      var now = staticCtx.currentTime;
      var level = STATIC_LEVEL_RATIO * stationVolumeLevel;
      var fade = Math.min(STATIC_FADE_S, clip.duration / 2);
      fadeGain.gain.setValueAtTime(0, now);
      fadeGain.gain.linearRampToValueAtTime(level, now + fade);
      fadeGain.gain.setValueAtTime(level, now + clip.duration - fade);
      fadeGain.gain.linearRampToValueAtTime(0, now + clip.duration);

      source.connect(fadeGain);
      fadeGain.connect(staticMuteGain);

      source.start(now, clip.start, clip.duration);
      staticSource = source;
      staticFadeGain = fadeGain;
      source.onended = function () {
        var wasCurrent = (staticSource === source);
        if (wasCurrent) { staticSource = null; staticFadeGain = null; }
        if (wasCurrent && isStaticChainAllowed(forToken)) {
          staticChainCount++;
          playStatic(forToken); // attempt still genuinely pending -- chain another bag clip rather than leaving silence
        }
      };
    } catch (e) {}
  }

  // Graceful, public teardown -- called from every real exit path (see
  // the bugfix comment above playStatic() for the full list). Ramps to
  // silence over STATIC_STOP_FADE_S rather than hard-cutting (avoids a
  // click), then hard-stops + disconnects once the ramp completes.
  // stopStatic() itself is idempotent -- safe to call when nothing is
  // playing (most teardown paths call it unconditionally rather than
  // checking first).
  function stopStatic() {
    if (!staticCtx || !staticSource) { return; }
    try {
      var now = staticCtx.currentTime;
      if (staticFadeGain) {
        staticFadeGain.gain.cancelScheduledValues(now);
        staticFadeGain.gain.setValueAtTime(staticFadeGain.gain.value, now);
        staticFadeGain.gain.linearRampToValueAtTime(0, now + STATIC_STOP_FADE_S);
      }
      var src = staticSource;
      var gain = staticFadeGain;
      setTimeout(function () {
        try { src.stop(); } catch (e2) {}
        try { src.disconnect(); } catch (e2) {}
        if (gain) { try { gain.disconnect(); } catch (e2) {} }
      }, Math.ceil(STATIC_STOP_FADE_S * 1000) + 5);
    } catch (e) {}
    staticSource = null;
    staticFadeGain = null;
  }

  // Converts an ISO 3166-1 alpha-2 code ("SE", "us", ...) into the two
  // Unicode Regional Indicator Symbol codepoints that combine into a
  // single flag glyph on platforms with color-emoji flag support.
  // Anything that isn't exactly two letters (missing/malformed data)
  // yields '' rather than garbage -- callers treat that the same as "no
  // flag" (see .eva-radio-widget__station-flag:empty in the CSS).
  function countryCodeToFlagEmoji(countryCode) {
    if (!countryCode || typeof countryCode !== 'string') { return ''; }
    var upper = countryCode.trim().toUpperCase();
    if (!/^[A-Z]{2}$/.test(upper)) { return ''; }
    var first = 0x1F1E6 + (upper.charCodeAt(0) - 65);
    var second = 0x1F1E6 + (upper.charCodeAt(1) - 65);
    return String.fromCodePoint(first, second);
  }

  // Flag-render detection (2026-08-07): a platform without the regional-
  // indicator ligature (older Windows, mainly -- confirmed live: Radio
  // Falastini's PS renders as a real flag on Apple/Android, a tofu box on
  // unpatched Windows) can't be told apart from a working one just by
  // reading the string -- both are valid Unicode, the difference is
  // purely whether the font/OS combines the two codepoints into one
  // glyph. Detected once via a canvas PIXEL-COLOR check, not a width
  // comparison -- width comparison was the first approach tried here and
  // it produced a real, confirmed false positive: modern text-shaping
  // engines already group a regional-indicator PAIR into one "extended
  // grapheme cluster" for cursor/layout purposes independent of whether a
  // color-emoji glyph actually exists for it, so an unsupported platform
  // can still measure the combined pair at the SAME width as a single
  // character (tested live: a font-less headless browser measured both
  // at exactly 100 -- a width-only check would have wrongly called that
  // "supported"). Pixel color is the real tell instead: a genuine color-
  // emoji flag glyph has saturated, non-grayscale pixels (reds/blues/
  // whatever the flag's own colors are); a missing-glyph "tofu" box or a
  // plain fallback letter is grayscale -- every sampled pixel's R/G/B
  // channels stay close together. Scanning for ANY pixel with real
  // channel divergence tells "the OS drew a real colored glyph" from
  // "the OS drew a placeholder," which the width check couldn't. Probed
  // once with a single arbitrary flag (US) and cached -- this is a
  // platform/font-level capability, not a per-flag one, so one probe
  // correctly predicts every other two-letter combination. Never risk a
  // tofu box: the plain 2-letter code (styled in the LCD's own font, not
  // the emoji stack) is always a safe fallback, so detection failing
  // closed (assume NOT supported) on any error or ambiguity is the
  // correct default, not the reverse.
  var flagEmojiSupported = null;

  function detectFlagEmojiSupport() {
    if (flagEmojiSupported !== null) { return flagEmojiSupported; }
    try {
      var size = 32;
      var canvas = document.createElement('canvas');
      canvas.width = size;
      canvas.height = size;
      var ctx = canvas.getContext('2d');
      if (!ctx) { flagEmojiSupported = false; return false; }
      ctx.textBaseline = 'top';
      ctx.font = (size - 4) + 'px "Segoe UI Emoji", "Noto Color Emoji", "Apple Color Emoji", sans-serif';
      ctx.fillText(String.fromCodePoint(0x1F1FA, 0x1F1F8), 0, 0); // US, arbitrary probe -- see comment above
      var data = ctx.getImageData(0, 0, size, size).data;
      var found = false;
      for (var i = 0; i < data.length; i += 4) {
        if (data[i + 3] < 32) { continue; } // skip near-transparent samples
        var r = data[i], g = data[i + 1], b = data[i + 2];
        var maxc = Math.max(r, g, b), minc = Math.min(r, g, b);
        if (maxc - minc > 30) { found = true; break; } // real color divergence -- not grayscale
      }
      flagEmojiSupported = found;
    } catch (e) {
      flagEmojiSupported = false; // fails closed -- see comment above
    }
    return flagEmojiSupported;
  }

  function setFlag(countryCode) {
    if (!flagEl) { return; }
    var upper = (countryCode || '').trim().toUpperCase();
    var isValid = /^[A-Z]{2}$/.test(upper);
    if (!isValid) {
      flagEl.textContent = '';
      flagEl.classList.remove('eva-radio-widget__station-flag--text');
      return;
    }
    if (detectFlagEmojiSupport()) {
      flagEl.textContent = countryCodeToFlagEmoji(upper);
      flagEl.classList.remove('eva-radio-widget__station-flag--text');
    } else {
      flagEl.textContent = upper; // plain 2-letter code -- never a tofu box
      flagEl.classList.add('eva-radio-widget__station-flag--text'); // switches to the LCD's own font, see that CSS rule
    }
  }

  // countryCode is optional -- every state that isn't "a specific station
  // is locked" (OFF, NO STATIONS, TUNING..., SCANNING..., NO SIGNAL) is
  // called with just the text, which correctly clears any flag left over
  // from whatever was showing before.
  function setReadout(text, countryCode) {
    if (readout) { readout.textContent = text; }
    setFlag(countryCode);
  }

  function setControlsEnabled(enabled) {
    // BACK is gated on real listening history existing (see historyStack
    // in the Presets section below), not on preset count -- with zero
    // history there is nothing to retrace regardless of how many
    // stations are on the dial. FORWARD/SCAN both need >=2 presets to
    // have anywhere else to roll to.
    if (prevBtn) { prevBtn.disabled = !enabled || historyStack.length === 0; }
    if (nextBtn) { nextBtn.disabled = !enabled || presets.length < 2; }
    if (scanBtn) { scanBtn.disabled = !enabled || presets.length < 2; }
  }

  // Plays url on the station audio element; calls cb(true) once real
  // playback starts, cb(false) on error or on a hard timeout (a stalled
  // connection that never errors is exactly the "stuck spinner" this
  // guards against -- see ATTEMPT_TIMEOUT_MS above). Only ever one
  // attempt "live" at a time -- attemptToken lets a superseded attempt's
  // late event/timeout no-op instead of stomping a newer one (e.g. the
  // visitor mashes NEXT twice quickly).
  function attemptPlay(url, cb) {
    // Exclusivity, take 3 (2026-08-07): turnOn() already stops any
    // playing tape before this ever gets called through the normal UI
    // (the SCAN/TUNE controls aren't even reachable until station mode
    // is on, which is exactly when that happens) -- but this is every
    // path that actually starts station audio (TUNE and SCAN both route
    // through here), so checking here too is the one place that
    // guarantees it regardless of how playback gets triggered, rather
    // than trusting every caller to have gone through turnOn() first.
    // Take 2 called stop() unconditionally on every attempt -- harmless
    // to the AUDIO (stop() on an already-stopped deck is a no-op there),
    // but stop() always dispatches eva:radio:playstate with
    // reason:'stop', which eva-radio-tape.js turns into a visible
    // cassette-door pop flash regardless of whether anything was
    // actually playing. A SCAN with several failing candidates calls
    // attemptPlay() once per retry, so that flashed the TAPE DECK
    // repeatedly for something happening entirely in the tuner -- wrong
    // place, per Per ("the deck is for cassettes"). isPlaying() (a
    // side-effect-free read) means stop() -- and its door-pop -- now
    // only fires on the one retry where a tape genuinely WAS playing,
    // which in the real UI flow never happens during a SCAN sequence at
    // all (turnOn() already stopped it before SCAN became reachable).
    if (window.EvaRadioWidget && typeof window.EvaRadioWidget.isPlaying === 'function' && window.EvaRadioWidget.isPlaying()) {
      window.EvaRadioWidget.stop();
    }
    var token = ++attemptToken; // established BEFORE playStatic() below -- it needs this attempt's own token to know (via isStaticChainAllowed()) whether it's still covering a live attempt at each chain link
    staticChainCount = 0; // fresh cap budget for this attempt -- see MAX_STATIC_CHAIN_CLIPS
    playStatic(token); // one clip per attempt, chaining into more of its own accord while genuinely pending -- see its own comment; covers TUNE and SCAN (including each retried candidate) from this single call site
    var settled = false;
    var timer = null;

    function finish(ok) {
      if (settled || token !== attemptToken) { return; }
      settled = true;
      clearTimeout(timer);
      stopStatic(); // never overlap the incoming stream -- ends here whether this attempt succeeded or failed/timed out
      stationAudio.removeEventListener('playing', onPlaying);
      stationAudio.removeEventListener('error', onError);
      cb(ok);
    }
    function onPlaying() { finish(true); }
    function onError() { finish(false); }

    stationAudio.addEventListener('playing', onPlaying);
    stationAudio.addEventListener('error', onError);
    timer = setTimeout(function () { finish(false); }, ATTEMPT_TIMEOUT_MS);

    try {
      stationAudio.src = url;
      stationAudio.load();
      var p = stationAudio.play();
      if (p && typeof p.catch === 'function') { p.catch(function () { finish(false); }); }
    } catch (e) {
      finish(false);
    }
  }

  function stopStation() {
    attemptToken++; // invalidate any in-flight attempt -- also what isStaticChainAllowed() checks, so this alone stops a static chain from extending further
    stopStatic(); // BUG FIX (2026-08-09): also cut off whatever clip is playing RIGHT NOW, immediately -- bumping attemptToken alone only stops the chain from continuing at its NEXT link, it doesn't touch a clip already in flight. This is the actual fix for "RADIO off while static is playing" -- previously nothing on this path called stopStatic() at all, see the bugfix comment above playStatic().
    try { stationAudio.pause(); } catch (e) {}
  }

  // --- Presets (TUNE) ----------------------------------------------------
  // Shuffle bag (2026-08-08), supersedes random-per-press -- "random order,
  // but no station repeats until the entire list has been played through,"
  // per Per: independent random-per-press could hand back the same station
  // twice in a row (or leave one unheard for a long time by bad luck), a
  // real permutation queue can't. FORWARD and SCAN share the SAME bag (see
  // stationBag/createShuffleBag() below) -- pushing the outgoing station onto
  // historyStack first; BACK retraces that real history rather than
  // rolling its own pick -- "overshooting a good station and being unable
  // to get back to it is the worst version of this," per Per.

  // FIX (2026-08-09): was 4. Still within the "3-5 consecutive failures"
  // range the original spec allowed, tightened to the low end of it as
  // part of the same "~20s hang" fix as ATTEMPT_TIMEOUT_MS above -- the
  // real fix is turnOn()'s resumeThenBagChain() folding the resume
  // attempt INTO this same budget instead of trying it separately first
  // (old worst case: one bare attempt + a full fresh chain = up to
  // (1+4)*6000ms = 30s; new worst case, any path: 3*5000ms = 15s, a hard
  // ceiling, not just a typical case).
  var MAX_AUTO_SKIP_ATTEMPTS = 3;

  // Generic shuffle-bag factory (2026-08-09, factored out of what was a
  // station-only refillBag()/nextFromBag() pair) -- a permutation queue
  // over 0..count()-1: every index served exactly once, in a fresh random
  // order, before any repeats. Per's explicit seam rule: the FIRST entry
  // of a new bag must not equal the LAST entry served from the bag that
  // just emptied, or the visitor hears the same thing twice in a row
  // across the reshuffle -- a plain per-cycle shuffle alone doesn't
  // guarantee that, since the new shuffle knows nothing about how the old
  // one ended. Takes a countFn rather than a fixed number so a bag can be
  // created before its own source list is populated (e.g. the station
  // bag, created at module init, well before loadManifest()'s fetch
  // resolves) and still always reflect the CURRENT length whenever it's
  // actually drawn from. "Consumes" on every draw regardless of what the
  // caller does with the result -- a dead entry an auto-skip chain
  // rejects is gone from THIS cycle's bag either way, never re-offered
  // until the next reshuffle, per Per: "a station [or clip] that fails
  // must be CONSUMED from the bag, not left to be re-offered later in the
  // same cycle." Shared by the station list (see stationBag below) and
  // the static-clip sprite (see getStaticClipBag() in the Tuning static
  // section above) -- per Per: "reuse it, don't write a second
  // implementation."
  function createShuffleBag(countFn) {
    var items = [];
    var lastServed = null;
    function refill() {
      var count = countFn();
      var pool = [];
      for (var i = 0; i < count; i++) { pool.push(i); }
      shuffleInPlace(pool);
      if (pool.length > 1 && lastServed !== null && pool[0] === lastServed) {
        var swapWith = 1 + Math.floor(Math.random() * (pool.length - 1));
        var tmp = pool[0]; pool[0] = pool[swapWith]; pool[swapWith] = tmp;
      }
      items = pool;
    }
    return {
      next: function () {
        if (!items.length) { refill(); }
        if (!items.length) { return -1; } // count() is 0
        var idx = items.shift();
        lastServed = idx;
        return idx;
      }
    };
  }

  var stationBag = createShuffleBag(function () { return presets.length; });

  // --- Media Session (lock-screen / control-center now-playing) ---------
  // Added 2026-08-09 -- see eva-radio.js's own copy of this same pattern
  // for the tape side. The two never fight over navigator.mediaSession
  // because they're already mutually exclusive by this file's own
  // existing architecture (see the header comment) -- whichever is
  // actually playing is always whichever one most recently wrote the
  // metadata, no shared coordinator needed.
  var mediaSessionSupported = 'mediaSession' in navigator;

  function updateStationMediaSession(station) {
    if (!mediaSessionSupported || !station) { return; }
    try {
      navigator.mediaSession.metadata = new MediaMetadata({
        title: 'Eva Radio',
        artist: station.name,
        // ITEM 3 AMENDMENT (2026-08-09): a dedicated square Eva Radio
        // artwork now, not the boombox-photo fallback this used before
        // (that stopgap -- fallbackArtworkUrl, resolved from
        // .eva-radio-widget__img in init() -- is gone entirely, along
        // with the never-populated station.image override it also
        // checked for; no station in the manifest has ever set one).
        // Same array the tape side uses -- see window.EvaRadioArtwork's
        // own comment in theme.liquid -- per Per's explicit "same
        // artwork for both, for now."
        artwork: window.EvaRadioArtwork || []
      });
      navigator.mediaSession.playbackState = 'playing';
    } catch (e) {}
  }

  function setStationMediaSessionPlaybackState(playbackState) {
    if (!mediaSessionSupported) { return; }
    try { navigator.mediaSession.playbackState = playbackState; } catch (e) {}
  }

  function clearStationMediaSession() {
    if (!mediaSessionSupported) { return; }
    try {
      navigator.mediaSession.metadata = null;
      navigator.mediaSession.playbackState = 'none';
    } catch (e) {}
  }

  // Single place that turns a chain's final result (a manifest index, or
  // -1 if every candidate in the chain failed) into UI state -- shared by
  // the initial tune, FORWARD/SCAN, and BACK's own auto-skip fallback so
  // "what happens when a chain resolves" is defined exactly once.
  function applyTuneResult(foundIdx) {
    setControlsEnabled(true);
    if (foundIdx < 0) {
      setReadout('-- NO SIGNAL --'); // the umbrella "nothing reachable" state now, not a single station's label -- see MAX_AUTO_SKIP_ATTEMPTS above
      hideTunerScan();
      return;
    }
    presetIndex = foundIdx;
    var station = presets[foundIdx];
    setReadout(station.name, station.countryCode);
    updateStationMediaSession(station);
    settleTunerScan();
  }

  function beginTuning() {
    setReadout('TUNING...');
    setControlsEnabled(false);
  }

  // FIX (2026-08-09, "~20s hang" bug report, part 2): every multi-attempt
  // chain below now shows which attempt it's on ("TUNING... (2/3)") --
  // previously the readout just said "TUNING..." for the ENTIRE chain,
  // so a slow multi-station retry (worst case, before this fix, up to
  // 30s -- see MAX_AUTO_SKIP_ATTEMPTS' own comment) looked identical to
  // a frozen UI even though static was audibly playing and the tuner-
  // scan dial was visibly jittering the whole time. Per's own report
  // called it a "hang," which a silent-looking wait absolutely reads as
  // regardless of what's technically still running underneath.
  function tuneStatusText(loadingText, attemptNum, totalAttempts) {
    return totalAttempts > 1 ? (loadingText + ' (' + attemptNum + '/' + totalAttempts + ')') : loadingText;
  }

  // Pulls up to totalAttempts entries from the shuffle bag, trying each
  // until one plays or the cap is hit -- "the visitor never sees a dead
  // slot," per Per's NO-SIGNAL rule. Shared by FORWARD, SCAN, and (via
  // resumeThenBagChain() below) turnOn()'s resume path, all now drawing
  // from the exact same bag and the exact same attempt budget. One
  // flicker/static burst covers the WHOLE chain (startTunerScan() fires
  // once, inside withMinVisibleTuning, not once per sub-attempt).
  // totalAttempts is passed through separately from attemptsLeft purely
  // so the progress text stays correct when resumeThenBagChain() hands
  // off a PARTIAL budget (attemptsLeft < totalAttempts) after its own
  // first try.
  function bagChain(attemptsLeft, totalAttempts, loadingText, cb) {
    if (attemptsLeft <= 0) { cb(-1); return; }
    setReadout(tuneStatusText(loadingText, totalAttempts - attemptsLeft + 1, totalAttempts));
    var idx = stationBag.next();
    if (idx < 0) { cb(-1); return; }
    attemptPlay(presets[idx].streamUrl, function (ok) {
      if (ok) { cb(idx); } else { bagChain(attemptsLeft - 1, totalAttempts, loadingText, cb); }
    });
  }

  // Shared by FORWARD and SCAN -- history push/no-push is each caller's
  // own decision (see tuneForward()/doScan()), everything after that is
  // identical: show loadingText, disable controls, pull from the bag
  // with auto-skip, land on applyTuneResult(). turnOn()'s own resume
  // path does NOT go through this any more -- see resumeThenBagChain().
  function tuneFromBag(loadingText) {
    setReadout(loadingText);
    setControlsEnabled(false);
    withMinVisibleTuning(function (cb) {
      bagChain(MAX_AUTO_SKIP_ATTEMPTS, MAX_AUTO_SKIP_ATTEMPTS, loadingText, cb);
    }, applyTuneResult);
  }

  // turnOn()'s resume path ONLY (2026-08-09 fix): tries the station that
  // was playing before RADIO was last turned off ONCE, then -- only if
  // that specific attempt fails -- falls into the SAME bagChain() for
  // the REMAINING budget, rather than a separate full-length chain
  // afterward. This is the actual fix for the reported "~20s hang":
  // pressing RADIO while a station had already been tuned this session
  // used to mean one bare attempt (up to the old 6s timeout) THEN, only
  // on failure, a completely fresh full chain (up to 4 more * 6s) --
  // worst case ~30s, silently. Folding the resume attempt into attempt
  // #1 of the SAME bounded budget caps the true worst case at
  // MAX_AUTO_SKIP_ATTEMPTS * ATTEMPT_TIMEOUT_MS no matter which path got
  // you there, and it's visible (progress text) throughout either way.
  function resumeThenBagChain(resumeIdx, loadingText, cb) {
    var totalAttempts = MAX_AUTO_SKIP_ATTEMPTS;
    setReadout(tuneStatusText(loadingText, 1, totalAttempts));
    attemptPlay(presets[resumeIdx].streamUrl, function (ok) {
      if (ok) { cb(resumeIdx); } else { bagChain(totalAttempts - 1, totalAttempts, loadingText, cb); }
    });
  }

  function tuneForward() {
    if (!presets.length) { setReadout('NO STATIONS'); return; }
    if (presetIndex >= 0) { historyStack.push(presetIndex); }
    tuneFromBag('TUNING...');
  }

  // Pops historyStack and retries further back on a dead entry (capped at
  // MAX_AUTO_SKIP_ATTEMPTS pops) before giving up -- a station that died
  // since it was last heard shouldn't strand BACK on a NO SIGNAL when an
  // earlier real history entry might still work. Deliberately untouched by
  // the shuffle-bag rework -- history is its own record of what actually
  // played, walking it backwards doesn't touch the bag at all (a station
  // BACK replays isn't "re-served" from the bag's point of view).
  function tuneBackChain(attemptsLeft, totalAttempts, cb) {
    if (!historyStack.length || attemptsLeft <= 0) { cb(-1); return; }
    setReadout(tuneStatusText('TUNING...', totalAttempts - attemptsLeft + 1, totalAttempts));
    var idx = historyStack.pop();
    attemptPlay(presets[idx].streamUrl, function (ok) {
      if (ok) { cb(idx); } else { tuneBackChain(attemptsLeft - 1, totalAttempts, cb); }
    });
  }

  function tuneBack() {
    if (!historyStack.length) { return; } // BACK is disabled whenever this is empty -- see setControlsEnabled()
    beginTuning();
    withMinVisibleTuning(function (cb) {
      tuneBackChain(MAX_AUTO_SKIP_ATTEMPTS, MAX_AUTO_SKIP_ATTEMPTS, cb);
    }, applyTuneResult);
  }

  // --- SCAN ----------------------------------------------------------
  // SCAN now draws from the exact SAME shuffle bag as FORWARD (2026-08-08,
  // per Per: "the no-repeat guarantee means SCAN can't be a free random
  // pick any more -- make SCAN draw from the same bag as the forward
  // arrow"). Functionally this makes SCAN identical to FORWARD -- same
  // selection, same auto-skip, same history push -- the button and the
  // tuner-scan sweep animation are the only things that still make it feel
  // distinct. Per is deciding whether to keep SCAN as a separate control
  // once he's heard that it no longer does anything FORWARD doesn't.

  // --- Tuner scan indicator (2026-08-07, both TUNE and SCAN as of the
  // same-day timing fix below) --------------------------------------------
  // Originally scoped to SCAN only ("while SCAN is hunting" was Per's
  // original wording) -- broadened same day to fire on arrow TUNE too
  // ("every station change gets the little dial dance"), once SCAN
  // became manifest-only and started resolving too fast to see the
  // animation at all against a fast/local stream. See
  // withMinVisibleTuning() below for how both paths now share it.
  // Sweep range/indicator width mirror the CSS defaults exactly (see
  // .eva-radio-widget__tuner-scan's own comment for how they were
  // measured against the real photo -- the black "FM STEREO TUNING
  // SCALE" bar, not the frequency-number rows above it) -- kept here as
  // plain numbers rather than read back out of CSS since JS needs to do
  // arithmetic on them every tick, not just apply them once.
  var TUNER_SCAN_TRACK_LEFT_PCT = 59.97;
  var TUNER_SCAN_TRACK_WIDTH_PCT = 25.99;
  var TUNER_SCAN_INDICATOR_WIDTH_PCT = 2.5;
  var TUNER_SCAN_JITTER_MS = 110; // fast enough to read as "hunting," slow enough each position/opacity is still visible
  var TUNER_SCAN_OPACITY_MIN = 0.3;
  var TUNER_SCAN_OPACITY_MAX = 0.85;
  var TUNER_SCAN_SETTLE_OPACITY = 0.8; // spec: "~80% opacity" once locked

  var tunerScanTimer = null;
  // Two independent reasons the indicator can be hidden: nothing to show
  // (idle/NO SIGNAL/off) vs. the wrong angle to show it on (the printed
  // scale only exists on the 'front' photo -- see the eva:radio:angle
  // listener in init, below). hidden = the OR of both, recomputed by
  // updateTunerScanVisibility() rather than set ad-hoc at each call site,
  // so the two reasons can never fight over the [hidden] attribute.
  var tunerScanShouldShow = false;
  var onFrontAngle = true;

  function updateTunerScanVisibility() {
    if (!tunerScanEl) { return; }
    tunerScanEl.hidden = !(tunerScanShouldShow && onFrontAngle);
  }

  function tunerScanMaxLeft() {
    return TUNER_SCAN_TRACK_LEFT_PCT + TUNER_SCAN_TRACK_WIDTH_PCT - TUNER_SCAN_INDICATOR_WIDTH_PCT;
  }

  function tunerScanRandomLeft() {
    var min = TUNER_SCAN_TRACK_LEFT_PCT;
    var max = tunerScanMaxLeft();
    return min + Math.random() * (max - min);
  }

  function tunerScanTick() {
    if (!tunerScanEl) { return; }
    tunerScanEl.style.setProperty('--eva-tuner-scan-left', tunerScanRandomLeft() + '%');
    tunerScanEl.style.opacity = (TUNER_SCAN_OPACITY_MIN + Math.random() * (TUNER_SCAN_OPACITY_MAX - TUNER_SCAN_OPACITY_MIN)).toFixed(2);
  }

  function startTunerScan() {
    if (!tunerScanEl) { return; }
    clearInterval(tunerScanTimer);
    tunerScanShouldShow = true;
    updateTunerScanVisibility();
    tunerScanTick(); // first frame lands immediately, not after the first interval delay
    tunerScanTimer = setInterval(tunerScanTick, TUNER_SCAN_JITTER_MS);
  }

  // Station locked -- stop jittering, rest at one final random spot
  // within the same range at the spec's steady ~80% opacity.
  function settleTunerScan() {
    if (!tunerScanEl) { return; }
    clearInterval(tunerScanTimer);
    tunerScanTimer = null;
    tunerScanShouldShow = true;
    updateTunerScanVisibility();
    tunerScanEl.style.setProperty('--eva-tuner-scan-left', tunerScanRandomLeft() + '%');
    tunerScanEl.style.opacity = TUNER_SCAN_SETTLE_OPACITY;
  }

  // NO SIGNAL, or leaving radio mode entirely -- nothing locked, nothing
  // to show.
  function hideTunerScan() {
    if (!tunerScanEl) { return; }
    clearInterval(tunerScanTimer);
    tunerScanTimer = null;
    tunerScanShouldShow = false;
    updateTunerScanVisibility();
  }

  var TUNER_SCAN_MIN_VISIBLE_MS = 600; // spec: "~600ms sweep" -- a floor, not a ceiling; a slow/failing real attempt just keeps animating past this, but a fast one (an already-fast-connecting manifest stream, or a quick failure) still gets a visible dial dance instead of an instant snap

  function nowMs() {
    return (window.performance && typeof performance.now === 'function') ? performance.now() : Date.now();
  }

  // Shared by TUNE (arrows) and SCAN so every station change gets the same
  // treatment: flicker starts immediately, runAttempt(cb) does the real
  // work (bagChain() pulling from the shuffle bag, for both TUNE and SCAN
  // alike now), and onSettled(result) only fires once
  // BOTH the real attempt has resolved AND at least TUNER_SCAN_MIN_VISIBLE_MS
  // has elapsed since the sweep began -- decoupling the visible animation
  // from however fast the underlying stream happens to connect (or fail).
  // stationAudio.volume is held at 0 for that same window (restored right
  // before onSettled runs) so a fast-connecting stream's own audio can't
  // become audible ahead of the dial settling/readout updating -- .volume
  // is deliberately untouched by EvaMute's own applyToElement() (see that
  // file's own "SILENCE, DON'T STOP" comment), so this doesn't fight the
  // global mute toggle; muted stays muted, unmuted stays silent-then-
  // audible exactly at the reveal.
  function withMinVisibleTuning(runAttempt, onSettled) {
    var start = nowMs();
    startTunerScan();
    try { stationAudio.volume = 0; } catch (e) {}
    runAttempt(function (result) {
      var wait = TUNER_SCAN_MIN_VISIBLE_MS - (nowMs() - start);
      function reveal() {
        try { stationAudio.volume = 1; } catch (e) {}
        onSettled(result);
      }
      if (wait > 0) { setTimeout(reveal, wait); } else { reveal(); }
    });
  }

  function doScan() {
    if (!presets.length) { setReadout('NO STATIONS'); return; }
    if (presetIndex >= 0) { historyStack.push(presetIndex); } // same behaviour as FORWARD now -- see the SCAN comment above
    tuneFromBag('SCANNING...');
  }

  // --- Mode toggle -----------------------------------------------------

  function showStationPanel() {
    if (panel) { panel.hidden = false; }
    if (tapeRack) { tapeRack.hidden = true; }
    if (rackHatch) { rackHatch.hidden = true; }
  }

  function hideStationPanel() {
    if (panel) { panel.hidden = true; }
    if (tapeRack) { tapeRack.hidden = false; }
  }

  function turnOn() {
    stationModeOn = true;
    if (toggleBtn) { toggleBtn.setAttribute('aria-pressed', 'true'); }
    if (window.EvaRadioWidget && typeof window.EvaRadioWidget.isPlaying === 'function' && window.EvaRadioWidget.isPlaying()) {
      window.EvaRadioWidget.stop(); // radio and tape never play at once -- see module comment. Gated on isPlaying() for the same reason attemptPlay() is -- no point flashing the deck's door-pop when nothing was playing to stop.
    }
    showStationPanel();
    if (!presets.length) {
      setReadout('NO STATIONS');
      setControlsEnabled(false);
    } else if (presetIndex >= 0) {
      // Resume exactly what was last tuned this visit -- a direct
      // re-attempt, not a fresh random roll (turning RADIO off/on isn't
      // "change the station"). Falls through to a bag draw, same as
      // FORWARD, only if the resume target has since died -- never
      // strands the visitor on NO SIGNAL for a station that simply isn't
      // being re-attempted. Deliberately doesn't push history here (same
      // as before the shuffle-bag rework) -- a died-since-last-visit
      // resume target was never really "played" this visit.
      //
      // FIX (2026-08-09): used to be a bare attemptPlay() here, THEN a
      // completely separate tuneFromBag() (its own full MAX_AUTO_SKIP_
      // ATTEMPTS chain) on failure -- two sequential worst-case waits
      // stacked on top of each other. resumeThenBagChain() folds the
      // resume attempt into attempt #1 of ONE shared budget instead --
      // see its own comment for the exact worst-case math this fixes.
      beginTuning();
      withMinVisibleTuning(function (cb) {
        resumeThenBagChain(presetIndex, 'TUNING...', cb);
      }, applyTuneResult);
    } else {
      // First tune of this visit -- a bag draw, not always the same
      // station, same mechanism as FORWARD.
      tuneFromBag('TUNING...');
    }
  }

  function turnOff() {
    stationModeOn = false;
    if (toggleBtn) { toggleBtn.setAttribute('aria-pressed', 'false'); }
    stopStation();
    hideStationPanel();
    hideTunerScan(); // "disappears when leaving radio mode" -- whether it was mid-jitter or settled on a lock, leaving radio mode always clears it
    clearStationMediaSession();
  }

  function toggle() {
    if (stationModeOn) { turnOff(); } else { turnOn(); }
  }

  // --- Manifest ----------------------------------------------------------

  // Fisher-Yates, in place. Used by createShuffleBag() (2026-08-08, see
  // the Presets section) to build each fresh shuffle-bag cycle, shared by
  // the station bag (FORWARD/SCAN) and the static-clip bag alike -- not
  // applied to the base `presets` array itself, whose order carries no
  // meaning, only membership does.
  function shuffleInPlace(arr) {
    for (var i = arr.length - 1; i > 0; i--) {
      var j = Math.floor(Math.random() * (i + 1));
      var tmp = arr[i]; arr[i] = arr[j]; arr[j] = tmp;
    }
    return arr;
  }

  function loadManifest() {
    var url = window.EvaRadioStationsManifestUrl;
    if (!url) { return; }
    fetch(url).then(function (r) {
      if (!r.ok) { throw new Error('stations manifest fetch failed'); }
      return r.json();
    }).then(function (data) {
      presets = (data && Array.isArray(data.stations)) ? data.stations : [];
      setControlsEnabled(stationModeOn);
    }).catch(function () {
      presets = [];
    });
  }

  // --- Init ----------------------------------------------------------

  ready(function () {
    toggleBtn = document.getElementById('EvaRadioStationToggle');
    panel = document.getElementById('EvaRadioStationPanel');
    readout = document.getElementById('EvaRadioStationReadout');
    flagEl = document.getElementById('EvaRadioStationFlag');
    prevBtn = document.getElementById('EvaRadioStationPrev');
    nextBtn = document.getElementById('EvaRadioStationNext');
    scanBtn = document.getElementById('EvaRadioStationScan');
    stationAudio = document.getElementById('EvaRadioStationAudio');
    tapeRack = document.getElementById('EvaRadioTapeRack');
    rackHatch = document.getElementById('EvaRadioRackHatch');
    tunerScanEl = document.getElementById('EvaRadioTunerScan');
    if (!toggleBtn || !panel || !stationAudio) { return; }

    if (window.EvaMute) { window.EvaMute.applyToElement(stationAudio); }
    initStaticSprite();

    stationAudio.addEventListener('play', function () { setStationMediaSessionPlaybackState('playing'); });
    stationAudio.addEventListener('pause', function () { setStationMediaSessionPlaybackState('paused'); });

    if (mediaSessionSupported) {
      try {
        navigator.mediaSession.setActionHandler('play', function () {
          var p = stationAudio.play();
          if (p && typeof p.catch === 'function') { p.catch(function () {}); }
        });
        navigator.mediaSession.setActionHandler('pause', function () { stationAudio.pause(); });
        // TUNE arrows are a real, natural fit for skip-forward/back --
        // unlike the tape side, this one gets both handlers.
        navigator.mediaSession.setActionHandler('nexttrack', function () { tuneForward(); });
        navigator.mediaSession.setActionHandler('previoustrack', function () { tuneBack(); });
      } catch (e) {}
    }

    loadManifest();
    setControlsEnabled(false);

    toggleBtn.addEventListener('click', toggle);
    if (prevBtn) { prevBtn.addEventListener('click', tuneBack); }
    if (nextBtn) { nextBtn.addEventListener('click', tuneForward); }
    if (scanBtn) { scanBtn.addEventListener('click', doScan); }

    // A tape starting (rack PLAY/drag-insert/Enter) always wins -- this
    // is a pure safety net (the rack is already hidden/unreachable while
    // the station panel is showing, see showStationPanel()), not the
    // primary way exclusivity is enforced.
    document.addEventListener('eva:radio:playstate', function (e) {
      if (e.detail && e.detail.playing) {
        stopStatic(); // BUG FIX (2026-08-09), Part 3: tape starting must kill static immediately, unconditionally -- not only as a side effect of stationModeOn happening to be true at this exact instant below. Idempotent/harmless if nothing's playing.
      }
      if (e.detail && e.detail.playing && stationModeOn) {
        stationModeOn = false;
        if (toggleBtn) { toggleBtn.setAttribute('aria-pressed', 'false'); }
        stopStation();
        hideStationPanel();
        hideTunerScan();
      }
    });

    // The tuner-scan indicator's printed scale only exists on the
    // 'front' photo -- eva-radio.js dispatches this on every angle
    // change (door-changer tab, desktop shortcuts, etc.), independent of
    // radio/tape mode, so this is the one place that needs to know about
    // it regardless of what else is going on.
    document.addEventListener('eva:radio:angle', function (e) {
      onFrontAngle = !e.detail || e.detail.angle === 'front';
      updateTunerScanVisibility();
    });
  });

  window.EvaRadioStation = {
    isOn: function () { return stationModeOn; },
    turnOn: turnOn,
    turnOff: turnOff,
    scan: doScan,
    tuneForward: tuneForward,
    tuneBack: tuneBack
  };
})();
